import * as zod from 'zod';

export const FeatureFlagDataZod = zod.object({
  classifyStep: zod.boolean().default(false),
  locateStep: zod.boolean().default(false),
  waypoints: zod.boolean().default(false),
  adHocFullSpeed: zod.boolean().default(false),
  dualModeCollisionDisabled: zod.boolean().default(false),
  disableEstopHealthCheck: zod.boolean().default(false),
  jerkLimit: zod.boolean().default(false),
  enableScrewdriverPickup: zod.boolean().default(false),
  cameraRestartButton: zod.boolean().default(false),
  useHighResStream: zod.boolean().default(true),
  lowCameraFPS: zod.boolean().default(false),
  enableDepthStream: zod.boolean().default(false),
  stereoVision: zod.boolean().default(false),
  enableDeveloperAPI: zod.boolean().default(false),
  guidedRoutines: zod.boolean().default(false),
  welcomeGuides: zod.boolean().default(false),
  enableJointLevelFirmwareUpdates: zod.boolean().default(false),
  darkMode: zod.boolean().default(false),
  adjustValuesOnInvalid: zod.boolean().default(false),
  skills: zod.boolean().default(false),
  recordROSBag: zod.boolean().default(false),
  complianceMode: zod.boolean().default(false),
  visualizePositionListsWhenNotEditing: zod.boolean().default(false),
  miniArm: zod.boolean().default(false),
  enableFirmwareAutoUpdate: zod.boolean().default(false),
  ros2: zod.boolean().default(true),
  rosScalingSpeedLimits: zod.boolean().default(false),
  disableAverageTorqueLimit: zod.boolean().default(false),
  activityTimer: zod.boolean().default(false),
  activityTimerReducedTime: zod.boolean().default(false),
  enableFirmwareMismatch: zod.boolean().default(false),
  newCNCIntegration: zod.boolean().default(false),
  ewellixLiftTLT: zod.boolean().default(false),
  schunkEGxGripper: zod.boolean().default(false),
  refactorModbus: zod.boolean().default(false),
  logModbusMessages: zod.boolean().default(false),
  legacyHaasSteps: zod.boolean().default(false),
  factoryReset: zod.boolean().default(false),
  weldMachineIntegration: zod.boolean().default(false),
  allowHighAcceleration: zod.boolean().default(false),
  rebootAfterSoftwareUpdate: zod.boolean().default(true),
  enableCodeBlocks: zod.boolean().default(false),
  safetyOuts: zod.boolean().default(false),
  localAccuracyCalibration: zod.boolean().default(false),
  customDevices: zod.boolean().default(false),
  customBase: zod.boolean().default(false),
  pythonExpressions: zod.boolean().default(false),
  backups: zod.boolean().default(false),
  systemMonitorShutdown: zod.boolean().default(false),
  etherNetIP: zod.boolean().default(false),
  tooltipAcceleration: zod.boolean().default(false),
  arcStep: zod.boolean().default(false),
  onRobotHexE: zod.boolean().default(false),
  antigravityToggle: zod.boolean().default(false),
  locateStepTextPrompt: zod.boolean().default(false),
  palletizing: zod.boolean().default(false),
  ros2Bridge: zod.boolean().default(false),
  allowHighTorque: zod.boolean().default(false),
  thor: zod.boolean().default(false),
  allowCustomCameraConfiguration: zod.boolean().default(false),
  useHardwareConfigs: zod.boolean().default(false),
  zmqPoseMigration: zod.boolean().default(false),
  useControlSourceManager: zod.boolean().default(false),
  enableFoxgloveServer: zod.boolean().default(false),
  treatEncoderIssuesAsWarnings: zod.boolean().default(false),
});

export type FeatureFlagData = zod.infer<typeof FeatureFlagDataZod>;

export type FeatureFlagKey = keyof FeatureFlagData;

type Section =
  | 'vision'
  | 'movement'
  | 'equipment'
  | 'infra'
  | 'safety'
  | 'newUI'
  | 'AI'
  | 'other'
  | 'environmentAndSpace';

export type FeatureFlagDefinitionSchema = {
  title: string;
  allowSingleUser?: boolean; // allow to be used by one user - only works for browser based flags
  section?: Section;
  description?: string;
};

export const featureFlagDefinitions: Record<
  FeatureFlagKey,
  FeatureFlagDefinitionSchema
> = {
  cameraRestartButton: {
    title: 'Camera restart button',
    section: 'vision',
    description:
      'Show button to restart camera by running rtcwake command on host',
    allowSingleUser: true,
  },
  classifyStep: {
    title: 'Classify Step',
    section: 'vision',
    description: 'Classify/Categorize what the camera sees',
    allowSingleUser: true,
  },
  ros2: {
    title: 'Enable ROS2',
    description: 'Enable ROS2 motions and migrations',
    section: 'movement',
    allowSingleUser: false,
  },
  rosScalingSpeedLimits: {
    title: 'Use ROS scaling for speed limits',
    description:
      'Use single scaling factor for velocity and acceleration instead of individual joint limits',
    section: 'movement',
    allowSingleUser: false,
  },
  disableAverageTorqueLimit: {
    title: 'Disable average torque limit',
    description:
      'Disables the average torque limit validation when running motions',
    section: 'movement',
    allowSingleUser: false,
  },
  locateStep: {
    title: 'Locate Step',
    section: 'vision',
    description: 'Find objects in 3D space',
    allowSingleUser: true,
  },
  useHighResStream: {
    title: 'Use high resolution camera stream',
    section: 'vision',
    description: 'Use a higher resolution for the realsense camera stream',
    allowSingleUser: false,
  },
  lowCameraFPS: {
    title: 'Low camera FPS',
    section: 'vision',
    description: 'Lower the camera FPS to reduce hardware load',
    allowSingleUser: false,
  },
  enableDepthStream: {
    title: 'Enable Depth Stream',
    section: 'vision',
    description: 'Enables the depth stream on the Realsense wrist camera,',
    allowSingleUser: false,
  },
  stereoVision: {
    title: 'Stereo Vision',
    section: 'vision',
    description: 'Enable stereo vision capabilities using camera setup',
    allowSingleUser: true,
  },
  waypoints: { title: 'Waypoints', section: 'movement', allowSingleUser: true },
  arcStep: {
    title: 'Arc Step',
    description: 'Enable the new Arc Step',
    section: 'movement',
    allowSingleUser: false,
  },
  adHocFullSpeed: {
    title: 'Full Speed Adhoc Movements',
    section: 'movement',
    allowSingleUser: true,
  },
  dualModeCollisionDisabled: {
    title: 'Disable IMU acceleration threshold detection',
    section: 'safety',
    allowSingleUser: true,
  },
  disableEstopHealthCheck: {
    title: 'Disable pre-unbrake Estop check',
    section: 'safety',
    allowSingleUser: true,
  },
  safetyOuts: {
    title: 'Safety outputs',
    section: 'safety',
    allowSingleUser: true,
    description: 'Enable feature to assign safeguard rules to outputs',
  },
  jerkLimit: {
    title: 'Jerk limit',
    section: 'movement',
    allowSingleUser: true,
  },
  enableScrewdriverPickup: {
    title: 'OnRobot Screwdriver pickup',
    section: 'equipment',
    allowSingleUser: true,
  },
  enableDeveloperAPI: { title: 'Developer API', allowSingleUser: true },
  guidedRoutines: { title: 'Guided routine setup', allowSingleUser: true },
  welcomeGuides: { title: 'Welcome guides', allowSingleUser: true },
  darkMode: { title: 'Dark mode', allowSingleUser: true },
  enableJointLevelFirmwareUpdates: {
    title: 'Firmware update UI',
    section: 'infra',
    allowSingleUser: true,
  },
  adjustValuesOnInvalid: {
    title: 'Adjust trajectory points',
    description: 'Adjust trajectory points instead of erroring out',
    section: 'infra',
    allowSingleUser: false,
  },
  factoryReset: {
    title: 'Reset user data',
    description: 'Show button to Reset user data',
    section: 'infra',
    allowSingleUser: true,
  },
  skills: {
    title: 'Enable skills',
    description: 'Enable all AI features',
    section: 'AI',
  },
  recordROSBag: {
    title: 'Record ROS Bag',
    description: 'Enable ROS bag recording for robot state and trajectory data',
    section: 'infra',
    allowSingleUser: false,
  },
  complianceMode: {
    title: 'Compliance mode',
    description:
      'Run compliance controller as the pose-based real-time controller',
  },
  useHardwareConfigs: {
    title: 'Use Hardware Configurations',
    description: 'Use hardware configurations for AI tasks and recordings',
    section: 'AI',
  },
  miniArm: {
    title: 'Mini Arm',
    description: 'Add mini-arm control',
  },
  visualizePositionListsWhenNotEditing: {
    title: 'Visualize position lists when not editing',
    section: 'environmentAndSpace',
    allowSingleUser: true,
  },
  activityTimer: {
    title: 'Activity Timer',
    section: 'infra',
    allowSingleUser: true,
  },
  activityTimerReducedTime: {
    title: 'Activity Timer Reduced Time',
    section: 'infra',
    allowSingleUser: true,
  },
  enableFirmwareAutoUpdate: {
    title: 'Firmware auto update',
    section: 'infra',
    allowSingleUser: true,
  },
  enableFirmwareMismatch: {
    title: 'Firmware mismatch UI',
    section: 'infra',
    allowSingleUser: true,
  },
  newCNCIntegration: {
    title: 'New CNC integrations in equipment manager',
    section: 'equipment',
    allowSingleUser: true,
  },
  ewellixLiftTLT: {
    title: 'Ewellix Lift TLT',
    section: 'equipment',
    allowSingleUser: true,
  },
  schunkEGxGripper: {
    title: 'Schunk EGU/EGK grippers',
    section: 'equipment',
    allowSingleUser: true,
  },
  refactorModbus: {
    title: 'Refactored Modbus/Gripper',
    section: 'equipment',
    allowSingleUser: true,
  },
  logModbusMessages: {
    title: 'Log Modbus messages',
    section: 'equipment',
    allowSingleUser: true,
  },
  legacyHaasSteps: {
    title: 'Legacy Haas steps',
    description:
      'Include the legacy Haas steps "Monitor Haas machine" and "Run Haas program" in the step library',
    section: 'equipment',
    allowSingleUser: true,
  },
  weldMachineIntegration: {
    title: 'Weld machine integration',
    section: 'equipment',
    allowSingleUser: true,
  },
  allowHighAcceleration: {
    title: 'Allow high acceleration',
    description: 'Increase maximum acceleration for joints',
    allowSingleUser: true,
    section: 'safety',
  },
  rebootAfterSoftwareUpdate: {
    title: 'Reboot after software update',
    description: 'Reboots the control box after a software update',
    allowSingleUser: true,
    section: 'infra',
  },
  enableCodeBlocks: {
    title: 'Enable Code Blocks',
    description: 'Enable Code Blocks',
    section: 'infra',
  },
  localAccuracyCalibration: {
    title: 'Local Accuracy Calibration',
    section: 'environmentAndSpace',
    allowSingleUser: false,
    description:
      'Allows the user to calibrate the accuracy of the arm when moving to positions.',
  },
  customDevices: {
    title: 'Custom Devices',
    section: 'equipment',
    allowSingleUser: false,
    description: 'Enable custom devices',
  },
  customBase: {
    title: 'Custom Base',
    section: 'equipment',
    allowSingleUser: false,
    description: 'Enable custom robot base/mounting configuration',
  },
  etherNetIP: {
    title: 'EtherNet/IP',
    section: 'equipment',
    allowSingleUser: false,
    description: 'Enable EtherNet/IP adapter integration',
  },
  pythonExpressions: {
    title: 'Python Expressions',
    section: 'infra',
    allowSingleUser: false,
    description: 'Enable python expressions',
  },
  backups: {
    title: 'Backups',
    section: 'infra',
    allowSingleUser: false,
    description: 'Show backups option in robot menu',
  },
  systemMonitorShutdown: {
    title: 'System Monitor Shutdown',
    section: 'safety',
    allowSingleUser: true,
    description: 'Shut down control box when system reaches critical levels',
  },
  tooltipAcceleration: {
    title: 'Tooltip Acceleration',
    section: 'movement',
    description: 'Enable tooltip acceleration controls for safety settings',
  },
  antigravityToggle: {
    title: 'Antigravity Toggle',
    section: 'movement',
    allowSingleUser: true,
    description: 'Enable non-wrist antigravity toggle',
  },
  onRobotHexE: {
    title: 'OnRobot Hex-E',
    section: 'equipment',
    allowSingleUser: true,
    description: 'Enable OnRobot Hex-E integration',
  },
  locateStepTextPrompt: {
    title: 'Locate Step Text Prompting',
    section: 'vision',
    allowSingleUser: true,
    description:
      'Adds a text prompt to the locate step for recognition of objects in 3D space',
  },
  palletizing: {
    title: 'Palletizing',
    section: 'movement',
    allowSingleUser: true,
    description: 'Enable palletizing solution',
  },
  ros2Bridge: {
    title: 'ROS2 Bridge',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Enable `ros2-bridge-bot` to bridge ROS2 topics to the local network',
  },
  allowHighTorque: {
    title: 'For RO3 Only: Allow Increased Torque Safety Limits',
    description: 'Enable setting torque values above normal safety limits',
    section: 'safety',
    allowSingleUser: false,
  },
  thor: {
    title: 'Thor Robot Model',
    section: 'equipment',
    allowSingleUser: true,
    description:
      'Enable Thor robot model with updated torque limits and 3D visualization',
  },
  allowCustomCameraConfiguration: {
    title: 'Allow Custom Camera Configuration',
    section: 'equipment',
    allowSingleUser: false,
    description:
      'Enable editing of custom camera internal configuration, such as FPS and resolution',
  },
  zmqPoseMigration: {
    title: 'ZMQ Pose Migration',
    section: 'infra',
    allowSingleUser: true,
    description: 'Enable migration of pose messaging to ZMQ',
  },
  useControlSourceManager: {
    title: 'Control Source Manager',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Use the control source manager to manage what pose source can control the arm.',
  },
  enableFoxgloveServer: {
    title: 'Enable Foxglove Server',
    section: 'infra',
    allowSingleUser: false,
    description: 'Enable the Foxglove server',
  },
  treatEncoderIssuesAsWarnings: {
    title: 'Treat Encoder Issues as Warnings',
    section: 'safety',
    allowSingleUser: true,
    description: 'Treat encoder issues as warnings instead of errors',
  },
};

type GroupedKeys = { section: Section; keys: FeatureFlagKey[] }[];

export const groupedFeatureFlags: GroupedKeys = (() => {
  const grouped: GroupedKeys = [];

  for (const keyStr of Object.keys(featureFlagDefinitions)) {
    const key = keyStr as FeatureFlagKey;
    let { section } = featureFlagDefinitions[key];

    if (!section) {
      section = 'other';
    }

    const group = grouped.find((g) => g.section === section);

    if (group) {
      group.keys.push(key);
    } else {
      grouped.push({ section, keys: [key] });
    }
  }

  // sort by section
  return grouped.sort((a, b) => {
    if (a.section === 'other') {
      return 1;
    }

    if (b.section === 'other') {
      return -1;
    }

    return a.section.localeCompare(b.section);
  });
})();
